import { useState, useRef, useEffect, useCallback } from "react";
import { useTheme } from "@/lib/ThemeContext";

const IDLE_TIMEOUT = 3 * 60 * 1000; // 3 minutes idle
const CHARS = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEF";

export default function ScreenSaver() {
  const { tokens: t } = useTheme();
  const [active, setActive] = useState(false);
  const canvasRef = useRef(null);
  const idleTimer = useRef(null);
  const rafRef = useRef(null);
  const dropsRef = useRef([]);

  const resetTimer = useCallback(() => {
    if (active) { setActive(false); return; }
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setActive(true), IDLE_TIMEOUT);
  }, [active]);

  // Idle detection
  useEffect(() => {
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    idleTimer.current = setTimeout(() => setActive(true), IDLE_TIMEOUT);
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      clearTimeout(idleTimer.current);
    };
  }, [resetTimer]);

  // Dismiss on any interaction
  useEffect(() => {
    if (!active) return;
    const dismiss = () => setActive(false);
    const events = ["keydown", "mousedown", "touchstart"];
    // Small delay to prevent immediate dismiss
    const timer = setTimeout(() => {
      events.forEach((e) => window.addEventListener(e, dismiss, { once: true }));
    }, 300);
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, dismiss));
    };
  }, [active]);

  // Matrix rain animation
  useEffect(() => {
    if (!active || !canvasRef.current) { cancelAnimationFrame(rafRef.current); return; }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    const fontSize = 14;
    const cols = Math.floor(canvas.width / fontSize);

    if (dropsRef.current.length !== cols) {
      dropsRef.current = Array.from({ length: cols }, () => Math.random() * -50);
    }
    const drops = dropsRef.current;

    // Use theme accent color for the rain
    const rainColor = t.accent || "#00ff41";

    let lastTime = 0;
    const draw = (time) => {
      rafRef.current = requestAnimationFrame(draw);
      if (time - lastTime < 50) return; // ~20fps
      lastTime = time;

      ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < cols; i++) {
        const char = CHARS[Math.floor(Math.random() * CHARS.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Head character is bright
        ctx.fillStyle = rainColor;
        ctx.globalAlpha = 0.9;
        ctx.fillText(char, x, y);

        // Trail fades
        ctx.globalAlpha = 0.3;
        ctx.fillText(CHARS[Math.floor(Math.random() * CHARS.length)], x, y - fontSize);

        ctx.globalAlpha = 1;

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i] += 1;
      }
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [active, t.accent]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 z-[200] cursor-pointer" data-testid="screen-saver"
      onClick={() => setActive(false)}>
      <canvas ref={canvasRef} className="absolute inset-0" />
      <div className="absolute bottom-6 left-0 right-0 text-center pointer-events-none">
        <span className="font-retro text-[10px] tracking-widest animate-pulse"
          style={{ color: `${t.accent}40` }}>
          CLICK OR PRESS ANY KEY TO WAKE
        </span>
      </div>
    </div>
  );
}
